/**
 * 分析用データ取得APIクライアント
 * バックエンドの /{accountId}/analysis-data-extraction/* エンドポイントを呼び出す
 */

import { apiDelete, apiGet, apiPost } from './client';
import type {
  AnalysisDataExtractionDeleteResponse,
  AnalysisDataExtractionDownloadResponse,
  AnalysisDataExtractionJob,
  AnalysisDataExtractionJobListResponse,
  AnalysisDataExtractionMenuType,
  AnalysisDataExtractionStartResponse,
  DailyStoreSkuStartRequest,
  LatestDailyStoreSkuDefaultsResponse,
  LatestProductMasterDefaultsResponse,
  LatestStoreSkuSalesDatesDefaultsResponse,
  LatestStoreSkuSettingValuesDefaultsResponse,
  LatestWeeklyStoreProductGroupDefaultsResponse,
  PreviewRowCountResponse,
  ProductMasterStartRequest,
  StoreSkuSalesDatesStartRequest,
  StoreSkuSettingValuesStartRequest,
  WeeklyStoreProductGroupStartRequest,
} from '@/lib/types/feature/analysis-data-extraction';

/**
 * ジョブ一覧取得
 * menu_type で対象メニューを絞り込む（既定: weekly_store_product_group、上限50件）
 */
export async function listAnalysisDataExtractionJobs(
  accountId: string,
  authHeaders?: Record<string, string>,
  menuType: AnalysisDataExtractionMenuType = 'weekly_store_product_group',
  limit: number = 50,
): Promise<AnalysisDataExtractionJob[]> {
  const params = new URLSearchParams();
  params.append('menu_type', menuType);
  params.append('limit', limit.toString());

  const response = await apiGet<AnalysisDataExtractionJobListResponse>(
    `/${accountId}/analysis-data-extraction/jobs?${params.toString()}`,
    authHeaders,
  );
  return response.jobs;
}

/**
 * ジョブ単体取得
 */
export async function getAnalysisDataExtractionJob(
  accountId: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionJob> {
  return apiGet<AnalysisDataExtractionJob>(
    `/${accountId}/analysis-data-extraction/jobs/${jobId}`,
    authHeaders,
  );
}

/**
 * 結果ファイルの署名付きダウンロードURL取得
 */
export async function downloadAnalysisDataExtractionFile(
  accountId: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionDownloadResponse> {
  return apiGet<AnalysisDataExtractionDownloadResponse>(
    `/${accountId}/analysis-data-extraction/jobs/${jobId}/download`,
    authHeaders,
  );
}

/**
 * ジョブ削除
 */
export async function deleteAnalysisDataExtractionJob(
  accountId: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionDeleteResponse> {
  return apiDelete<AnalysisDataExtractionDeleteResponse>(
    `/${accountId}/analysis-data-extraction/jobs/${jobId}`,
    authHeaders,
  );
}

/**
 * [週次/月次] 店舗×商品グループ実績抽出の、ログインユーザーの前回実行条件を取得する
 */
export async function getLatestWeeklyStoreProductGroupDefaults(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestWeeklyStoreProductGroupDefaultsResponse> {
  return apiGet<LatestWeeklyStoreProductGroupDefaultsResponse>(
    `/${accountId}/analysis-data-extraction/weekly-store-product-group/latest-execution-defaults`,
    authHeaders,
  );
}

/**
 * [週次/月次] 店舗×商品グループ実績抽出の実行開始（202 Accepted でジョブIDが返る）
 */
export async function startWeeklyStoreProductGroupExtraction(
  accountId: string,
  request: WeeklyStoreProductGroupStartRequest,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionStartResponse> {
  return apiPost<AnalysisDataExtractionStartResponse>(
    `/${accountId}/analysis-data-extraction/weekly-store-product-group/start`,
    request,
    authHeaders,
  );
}

/**
 * [日別] 店舗×SKU 予測/実績/在庫抽出の、ログインユーザーの前回実行条件を取得する
 */
export async function getLatestDailyStoreSkuDefaults(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestDailyStoreSkuDefaultsResponse> {
  return apiGet<LatestDailyStoreSkuDefaultsResponse>(
    `/${accountId}/analysis-data-extraction/daily-store-sku/latest-execution-defaults`,
    authHeaders,
  );
}

/**
 * [日別] 店舗×SKU 予測/実績/在庫抽出の実行開始（202 Accepted でジョブIDが返る）
 */
export async function startDailyStoreSkuExtraction(
  accountId: string,
  request: DailyStoreSkuStartRequest,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionStartResponse> {
  return apiPost<AnalysisDataExtractionStartResponse>(
    `/${accountId}/analysis-data-extraction/daily-store-sku/start`,
    request,
    authHeaders,
  );
}

/**
 * 商品マスタ抽出の、ログインユーザーの前回実行条件を取得する
 */
export async function getLatestProductMasterDefaults(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestProductMasterDefaultsResponse> {
  return apiGet<LatestProductMasterDefaultsResponse>(
    `/${accountId}/analysis-data-extraction/product-master/latest-execution-defaults`,
    authHeaders,
  );
}

/**
 * 商品マスタ抽出（全件CSV）の実行開始（202 Accepted でジョブIDが返る）
 */
export async function startProductMasterExtraction(
  accountId: string,
  request: ProductMasterStartRequest,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionStartResponse> {
  return apiPost<AnalysisDataExtractionStartResponse>(
    `/${accountId}/analysis-data-extraction/product-master/start`,
    request,
    authHeaders,
  );
}

/**
 * 店舗×SKU 設定値リストの、ログインユーザーの前回実行条件を取得する
 */
export async function getLatestStoreSkuSettingValuesDefaults(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestStoreSkuSettingValuesDefaultsResponse> {
  return apiGet<LatestStoreSkuSettingValuesDefaultsResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-setting-values/latest-execution-defaults`,
    authHeaders,
  );
}

/**
 * 店舗×SKU 設定値リストの実行開始（202 Accepted でジョブIDが返る）
 */
export async function startStoreSkuSettingValuesExtraction(
  accountId: string,
  request: StoreSkuSettingValuesStartRequest,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionStartResponse> {
  return apiPost<AnalysisDataExtractionStartResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-setting-values/start`,
    request,
    authHeaders,
  );
}

/**
 * 店舗×SKU 初回/最終販売日・初回入庫日の、ログインユーザーの前回実行条件を取得する
 */
export async function getLatestStoreSkuSalesDatesDefaults(
  accountId: string,
  authHeaders?: Record<string, string>,
): Promise<LatestStoreSkuSalesDatesDefaultsResponse> {
  return apiGet<LatestStoreSkuSalesDatesDefaultsResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-sales-dates/latest-execution-defaults`,
    authHeaders,
  );
}

/**
 * 店舗×SKU 初回/最終販売日・初回入庫日の実行開始（202 Accepted でジョブIDが返る）
 */
export async function startStoreSkuSalesDatesExtraction(
  accountId: string,
  request: StoreSkuSalesDatesStartRequest,
  authHeaders?: Record<string, string>,
): Promise<AnalysisDataExtractionStartResponse> {
  return apiPost<AnalysisDataExtractionStartResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-sales-dates/start`,
    request,
    authHeaders,
  );
}

/**
 * 店舗×SKU 初回/最終販売日・初回入庫日の、実行前の件数プレビュー（同期）
 */
export async function previewStoreSkuSalesDatesRowCount(
  accountId: string,
  request: StoreSkuSalesDatesStartRequest,
  authHeaders?: Record<string, string>,
): Promise<PreviewRowCountResponse> {
  return apiPost<PreviewRowCountResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-sales-dates/preview-row-count`,
    request,
    authHeaders,
  );
}

/**
 * 店舗×SKU 設定値リストの、実行前の件数プレビュー（同期）
 */
export async function previewStoreSkuSettingValuesRowCount(
  accountId: string,
  request: StoreSkuSettingValuesStartRequest,
  authHeaders?: Record<string, string>,
): Promise<PreviewRowCountResponse> {
  return apiPost<PreviewRowCountResponse>(
    `/${accountId}/analysis-data-extraction/store-sku-setting-values/preview-row-count`,
    request,
    authHeaders,
  );
}
